import { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { Trash2, Loader2, Send } from "lucide-react";
import { storage, ID } from "../../../appwrite";

interface Message {
  $id: string;
  title: string;
  body: string;
  image_url?: string;
  $createdAt: string;
}

const BUCKET_ID =
  import.meta.env.VITE_APPWRITE_BUCKET_ID || "699cb21a00338ccf8778";

export const AdminMessages = () => {
  // const { user } = useAuth(); // Assuming auth context exists
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const res = await fetch("/api/messages");
      if (res.ok) {
        const data = await res.json();
        setMessages(data || []);
      }
    } catch (error) {
      console.error("Failed to fetch messages", error);
    } finally {
      setLoading(false);
    }
  };

  const handlesubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !body) return;

    setSubmitting(true);
    let imageUrl = "";

    try {
      if (file) {
        const uploadedFile = await storage.createFile(
          BUCKET_ID,
          ID.unique(),
          file,
        );
        // Get View URL
        imageUrl = storage.getFileView({
          bucketId: BUCKET_ID,
          fileId: uploadedFile.$id,
        });
      }

      const res = await fetch("/api/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, body, imageUrl }),
      });

      if (res.ok) {
        setTitle("");
        setBody("");
        setFile(null);
        fetchMessages();
      } else {
        alert("Failed to send message");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Error sending message");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this message?")) return;

    try {
      const res = await fetch("/api/messages", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messageId: id }),
      });

      if (res.ok) {
        fetchMessages();
      } else {
        alert("Failed to delete message");
      }
    } catch (error) {
      console.error("Error deleting message:", error);
    }
  };

  return (
    <AdminLayout>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
          Messages & Notifications
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Create Message Form */}
        <div className="lg:col-span-1">
          <div className="p-6 rounded-xl border border-border bg-card/50 backdrop-blur-sm">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Send className="w-5 h-5" /> Send New Message
            </h2>
            <form onSubmit={handlesubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full p-2 rounded-lg bg-background border border-border focus:border-primary outline-none transition-colors"
                  placeholder="Notification Title"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Message (Markdown)
                </label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  className="w-full p-2 h-32 rounded-lg bg-background border border-border focus:border-primary outline-none transition-colors"
                  placeholder="Use **bold**, *italic*, lists etc."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Image (Optional)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex justify-center items-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                  </>
                ) : (
                  "Send Push Notification"
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Messages List */}
        <div className="lg:col-span-2 space-y-4">
          <h2 className="text-xl font-semibold mb-4">History</h2>
          {loading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : messages.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No messages sent yet.
            </p>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.$id}
                className="p-4 rounded-xl border border-border bg-card/30 flex gap-4 hover:border-border/80 transition-colors"
              >
                {msg.image_url && (
                  <img
                    src={msg.image_url}
                    alt=""
                    className="w-24 h-24 object-cover rounded-lg bg-muted"
                  />
                )}
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <h3 className="font-semibold text-lg">{msg.title}</h3>
                    <button
                      onClick={() => handleDelete(msg.$id)}
                      className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                    {msg.body}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Sent: {new Date(msg.$createdAt).toLocaleString("it-IT")}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
};
